import type { FunctionComponent } from 'react';
import React, { useEffect, useRef } from 'react';

import { Col, Row } from 'src/components/ui';
import Form from 'src/components/ui/Form';

import reasonCancellationOptions from '../../data/reasonCancellationOptions';
import type {
  CustomValidityMessages,
  UpdateFieldType,
} from '../contactForm/ContactForm';
import style from '../contactForm/ContactForm.module.scss';

import type { ContactFormData } from 'src/models/contact';

import { INVALID_INPUT_FORMAT } from 'src/utils/Message.Utils';
import { mergeClassNames } from 'src/utils/ReactUtils';

type RequestCancelFormType = {
  updateField: UpdateFieldType;
  formValue: ContactFormData;
  customValidityMessages: CustomValidityMessages;
};

const RequestCancelForm: FunctionComponent<RequestCancelFormType> = ({
  updateField,
  formValue,
  customValidityMessages,
}) => {
  const registrationRef = useRef<HTMLInputElement>(null);
  const orderNumberRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      registrationRef.current &&
      formValue.registration &&
      customValidityMessages.registration !== ''
    ) {
      registrationRef.current.setCustomValidity(INVALID_INPUT_FORMAT);
    }
  }, [formValue.registration, customValidityMessages.registration]);

  useEffect(() => {
    if (
      orderNumberRef.current &&
      formValue.command &&
      customValidityMessages.command !== ''
    ) {
      orderNumberRef.current.setCustomValidity(INVALID_INPUT_FORMAT);
    }
  }, [formValue.command, customValidityMessages.command]);

  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group controlId="registration">
            <Form.Label
              className={mergeClassNames([
                style.contactFormLabel,
                style.contactFormLabelRequired,
              ])}
            >
              Immatriculation de votre véhicule
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="AA-123-BB *"
              required
              onChange={updateField}
              value={formValue.registration}
              name="registration"
              ref={registrationRef}
            />
            <Form.Control.Feedback type="invalid">
              {formValue.registration &&
              customValidityMessages.registration !== ''
                ? customValidityMessages.registration
                : 'Ce champ est obligatoire.'}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="command">
            <Form.Label
              className={mergeClassNames([
                style.contactFormLabel,
                style.contactFormLabelRequired,
              ])}
            >
              Numéro de votre commande
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="AD-AUTO-XXXXXXXX-XXXXXX *"
              required
              onChange={updateField}
              value={formValue.command}
              name="command"
              ref={orderNumberRef}
            />
            <Form.Control.Feedback type="invalid">
              {formValue.command && customValidityMessages.command !== ''
                ? customValidityMessages.command
                : 'Ce champ est obligatoire.'}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Form.Group controlId="reasonCancellation">
            <Form.Label
              className={mergeClassNames([
                style.contactFormLabel,
                style.contactFormLabelRequired,
              ])}
            >{`Merci de nous indiquer la raison afin de nous améliorer :`}</Form.Label>
            <Form.Control
              value={formValue.reasonCancellation}
              as="select"
              required
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                updateField(e, Number(e.target.value))
              }
              name="reasonCancellation"
            >
              <option value="">Sélectionnez...*</option>
              {reasonCancellationOptions.map((reasonCancellationOption) => (
                <option
                  key={reasonCancellationOption.id}
                  value={reasonCancellationOption.id}
                >
                  {reasonCancellationOption.title}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              Ce champ est obligatoire.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      {formValue.reasonCancellation === 4 && (
        <Row>
          <Col md={6}>
            <Form.Group controlId="motifCancellation">
              <Form.Label
                className={mergeClassNames([
                  style.contactFormLabel,
                  style.contactFormLabelRequired,
                ])}
              >
                Précision du motif
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Précision du motif *"
                required
                onChange={updateField}
                value={formValue.motifCancellation}
                name="motifCancellation"
              />
              <Form.Control.Feedback type="invalid">
                Ce champ est obligatoire.
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      )}
    </>
  );
};

export default RequestCancelForm;
